import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";
import Button from "../button/Button";
import ConfirmModal from "../modals/ConfirmModal";
import SearchInput from "@/components/forms/SearchInput";
import type { SchedulerToolbarProps } from "./SchedulerToolbar.types";
import { fetchScheduleCount } from "@/api";
import { normalize } from "@/lib/utils";

const SchedulerToolbar: React.FC<SchedulerToolbarProps> = (
    {
        solving,
        startedSolving,
        tabs,
        activeTab,
        onTabChange,
        onSolve,
        onAnalyze,
        onApply,
        onRefresh,
        onBenchmark,
        onImport,
        onDelete,
        onStop,
        progress = 0,
        isApplying,
        isAnalyzing,
        isRefreshing,
        isSaving,
        isStopping = false,
        refreshKey = 0,
        headerCollapsed = false,
        onToggleHeaderCollapse,
    }) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [options, setOptions] = useState<{ id: string, label: string }[]>([]);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<{ id: string; label: string } | null>(null);
    const [search, setSearch] = useState("");

    const setupSchedCount = async () => {
        const resp = await fetchScheduleCount()
        resp.sort((a, b) => parseInt(b.id, 10) - parseInt(a.id, 10))
        setOptions(resp)
        return resp
    }

    const handleLoad = (id: string) => {
        setPopoverOpen(false);
        setSearch("");
        Promise.resolve(onImport(id)).catch(() => {
        });
    }

    useEffect(() => {
        setupSchedCount().catch(() => {
        })
    }, [refreshKey])

    const normalizedSearch = normalize(search);
    const filteredOptions = [...options]
        .sort((a, b) => {
            return Number.parseInt(b.id, 10) - Number.parseInt(a.id, 10);
        })
        .filter((opt) => normalize(opt.label).includes(normalizedSearch));

    const displayProgress = Number.isFinite(progress)
        ? Math.max(0, Math.min(100, Math.round(progress)))
        : 0;

    return (
        <div className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center w-fit gap-2">
                    {onToggleHeaderCollapse && (
                        <Button
                            onClick={onToggleHeaderCollapse}
                            icon={<ChevronUp size={20}
                                             className={`transition-transform duration-300 ${headerCollapsed ? "rotate-180" : ""}`}/>}
                            type="secondary"
                            size="sm"
                            rounded
                        />
                    )}
                    {solving ? (
                        <>
                            <div
                                role="status"
                                aria-label={`Optimisation en cours, ${displayProgress}%`}
                                className="relative flex items-center justify-center h-10 w-10"
                            >
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"/>
                                <span className="absolute text-xs font-bold text-primary-600">
                                    {displayProgress}%
                                </span>
                            </div>
                            <Button
                                onClick={onStop}
                                label={isStopping ? "Arrêt…" : "Arrêter l'optimisation"}
                                type="danger"
                                size="sm"
                                rounded
                                disabled={isStopping}
                            />
                        </>
                    ) : (
                        <>
                            <Button onClick={onSolve} label={"Démarrer l'assignation"} type="primary" rounded
                                    style={{ backgroundColor: 'dodgerblue' }}/>
                            <Button
                                onClick={onRefresh}
                                label="Rafraîchir"
                                type="primary"
                                rounded
                                disabled={isRefreshing}
                                style={{ backgroundColor: '#309630' }}
                            />
                            <Button onClick={() => setConfirmOpen(true)} label={"Appliquer à l'horaire"} type="success"
                                    rounded
                                    disabled={isApplying}
                                    style={{ backgroundColor: '#198754' }}/>
                            <Button onClick={onAnalyze} label={isAnalyzing ? "Analyse…" : "Résultats"} rounded
                                    type="secondary" disabled={isAnalyzing}/>
                        </>
                    )}
                </div>
                <div className="flex flex-wrap items-center justify-end w-fit gap-1">
                    {tabs.map((tab) => (
                        <Button
                            key={tab.id}
                            onClick={() => onTabChange(tab.id)}
                            label={tab.label}
                            size="lg"
                            type={tab.id === activeTab ? "primary" : "transparent"}
                        />
                    ))}
                </div>
                <Button onClick={onBenchmark} label="Sauvegarde" rounded type="secondary"/>

                {options.length > 0 && (
                    <Popover
                        open={popoverOpen}
                        onOpenChange={(open) => {
                            setPopoverOpen(open);
                            if (!open) setSearch("");
                        }}
                    >
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="flex w-[240px] items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                            >
                                <span className="text-muted-foreground">Charger une sauvegarde</span>
                                <ChevronDown size={16} className="opacity-50"/>
                            </button>
                        </PopoverTrigger>

                        <PopoverContent className="w-[240px] max-h-72 overflow-y-auto">
                            <div className="sticky top-0 z-10 -mx-1 -mt-1 bg-popover px-1 py-1">
                                <SearchInput
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Rechercher..."
                                    aria-label="Rechercher une sauvegarde"
                                    autoFocus
                                />
                            </div>
                            {filteredOptions.length === 0 && (
                                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                                    Aucune sauvegarde trouvée
                                </div>
                            )}
                            {filteredOptions.map(opt => (
                                <div key={opt.id} className="flex items-center gap-1 rounded-sm hover:bg-accent">
                                    <button
                                        type="button"
                                        className="flex-1 truncate px-2 py-1.5 text-left text-sm outline-none"
                                        onClick={() => handleLoad(opt.id)}
                                    >
                                        {opt.label}
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={`Supprimer ${opt.label}`}
                                        className="shrink-0 rounded-sm px-2 py-1.5 text-muted-foreground outline-none hover:text-red-600 focus-visible:ring-2 focus-visible:ring-ring/50"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPendingDelete(opt);
                                        }}
                                    >
                                        <Trash2 size={14}/>
                                    </button>
                                </div>
                            ))}
                        </PopoverContent>
                    </Popover>
                )}

            </div>
            <ConfirmModal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={onApply}
                title="Confirmer l'application"
                message="Êtes-vous sûr de vouloir appliquer les changements à l'horaire ?"
                confirmDisabled={isApplying}
            />
            <ConfirmModal
                open={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={() => {
                    if (!pendingDelete) return;
                    const deletedId = pendingDelete.id;
                    onDelete(deletedId)
                        .then(() => setOptions((opts) => opts.filter((o) => o.id !== deletedId)))
                        .catch(() => {
                        });
                    setPendingDelete(null);
                }}
                title="Supprimer la sauvegarde"
                message={`Supprimer « ${pendingDelete?.label} » ? Cette action est irréversible.`}
                confirmLabel="Supprimer"
                confirmVariant="danger"
            />
        </div>
    );
};

export default SchedulerToolbar;
